/**
 * React hook для работы с аналитикой
 * Предоставляет удобный интерфейс для отправки событий из компонентов
 */

import { useCallback, useMemo } from 'react'

import { analytics } from './analytics'
import { isDebugMode } from './config'

import type { AnalyticsProduct } from './types'

type ProductLike = {
  id?: string | number | null
  name?: string | null
  xid?: string | null
  priceOffer?: {
    price?: number | null
    oldPrice?: number | null
  } | null
  brand?: {
    name?: string | null
  } | null
  categories?: Array<{
    name?: string | null
  } | null> | null
}

type CartItemLike = {
  id?: string | number | null
  amount?: number | null
  info?: ProductLike | null
}


/** Тип для товара из корзины или обычного товара */
export type ProductInput = ProductLike | CartItemLike

/**
 * Проверка валидности данных товара
 */
function isValidProductData (product: unknown): product is ProductLike {
  if (!product || typeof product !== 'object') return false
  const p = product as Record<string, unknown>
  return typeof p.id !== 'undefined' && typeof p.name === 'string' && p.name.length > 0
}

/**
 * Преобразование Product из приложения в AnalyticsProduct
 * Безопасно обрабатывает некорректные или неполные данные
 * Возвращает null для полностью невалидных данных
 */
export function toAnalyticsProduct (
  input: ProductInput | null | undefined,
  options?: {
    quantity?: number
    listName?: string
    listId?: string
    position?: number
  }
): AnalyticsProduct | null {
  try {
    // Проверка на null/undefined
    if (!input) {
      if (isDebugMode()) {
        console.warn('[Analytics] toAnalyticsProduct: input is null or undefined')
      }
      return null
    }

    // ExtendedCartItem имеет вложенный info: Product
    const product = 'info' in input ? input.info : input
    
    // Проверка на валидность product
    if (!isValidProductData(product)) {
      if (isDebugMode()) {
        console.warn('[Analytics] toAnalyticsProduct: invalid product data')
      }
      return null
    }

    const quantityValue = options?.quantity ?? ('amount' in input ? input.amount : 1)
    const quantity = typeof quantityValue === 'number' && quantityValue > 0 ? quantityValue : 1
    
    const price = product.priceOffer?.price ?? 0
    const oldPrice = product.priceOffer?.oldPrice ?? price
    const discount = oldPrice > price ? oldPrice - price : undefined
    
    // Собираем путь категории
    const categoryPath = product.categories
      ?.map((c) => c?.name)
      .filter(Boolean)
      .join('/') || undefined
    
    return {
      id: String(product.id),
      name: product.name as string,
      price,
      brand: product.brand?.name as string,
      category: categoryPath,
      quantity,
      sku: product.xid ?? undefined,
      discount,
      listName: options?.listName,
      listId: options?.listId,
      position: options?.position,
    }
  }
  catch (error) {
    if (isDebugMode()) {
      console.error('[Analytics] toAnalyticsProduct error:', error)
    }
    return null
  }
}

/**
 * Преобразование массива товаров
 * Фильтрует невалидные товары
 */
export function toAnalyticsProducts (
  products: ProductInput[],
  listName?: string,
  listId?: string
): AnalyticsProduct[] {
  return products
    .map((product, index) =>
      toAnalyticsProduct(product, {
        listName,
        listId,
        position: index,
      })
    )
    .filter((item): item is AnalyticsProduct => item !== null)
}

/**
 * Hook для работы с аналитикой
 */
export function useAnalytics () {
  // Ecommerce события
  const viewItemList = useCallback(
    (products: ProductInput[], listName?: string, listId?: string) => {
      const items = toAnalyticsProducts(products, listName, listId)
      if (items.length === 0) return
      
      analytics.viewItemList({
        listName,
        listId,
        items,
      })
    },
    []
  )

  const viewItem = useCallback((product: ProductInput) => {
    const item = toAnalyticsProduct(product)
    if (!item) return
    
    analytics.viewItem({ item })
  }, [])

  const selectItem = useCallback(
    (product: ProductInput, listName?: string, listId?: string) => {
      const item = toAnalyticsProduct(product, { listName, listId })
      if (!item) return
      
      analytics.selectItem({
        listName,
        listId,
        item,
      })
    },
    []
  )

  const addToCart = useCallback(
    (product: ProductInput, quantity: number = 1) => {
      const item = toAnalyticsProduct(product, { quantity })
      if (!item) return
      
      analytics.addToCart({ item })
    },
    []
  )

  const removeFromCart = useCallback(
    (product: ProductInput, quantity: number = 1) => {
      const item = toAnalyticsProduct(product, { quantity })
      if (!item) return
      
      analytics.removeFromCart({ item })
    },
    []
  )

  const viewCart = useCallback((products: ProductInput[]) => {
    const items = toAnalyticsProducts(products)
    if (items.length === 0) return
    
    const value = items.reduce(
      (sum, item) => sum + item.price * (item.quantity ?? 1),
      0
    )
    analytics.viewCart({ items, value })
  }, [])

  const beginCheckout = useCallback(
    (products: ProductInput[], coupon?: string) => {
      const items = toAnalyticsProducts(products)
      if (items.length === 0) return
      
      const value = items.reduce(
        (sum, item) => sum + item.price * (item.quantity ?? 1),
        0
      )
      analytics.beginCheckout({ items, value, coupon })
    },
    []
  )

  const addShippingInfo = useCallback(
    (products: ProductInput[], shippingTier: string, coupon?: string) => {
      const items = toAnalyticsProducts(products)
      if (items.length === 0) return
      
      const value = items.reduce(
        (sum, item) => sum + item.price * (item.quantity ?? 1),
        0
      )
      analytics.addShippingInfo({ items, value, shippingTier, coupon })
    },
    []
  )

  const addPaymentInfo = useCallback(
    (products: ProductInput[], paymentType: string, coupon?: string) => {
      const items = toAnalyticsProducts(products)
      if (items.length === 0) return
      
      const value = items.reduce(
        (sum, item) => sum + item.price * (item.quantity ?? 1),
        0
      )
      analytics.addPaymentInfo({ items, value, paymentType, coupon })
    },
    []
  )

  const purchase = useCallback(
    (
      transactionId: string,
      products: ProductInput[],
      options?: {
        tax?: number
        shipping?: number
        coupon?: string
      }
    ) => {
      const items = toAnalyticsProducts(products)
      if (items.length === 0) return
      
      const value = items.reduce(
        (sum, item) => sum + item.price * (item.quantity ?? 1),
        0
      )
      analytics.purchase({
        transactionId,
        items,
        value,
        ...options,
      })
    },
    []
  )

  // Кастомные события
  const search = useCallback((searchTerm: string, resultsCount?: number) => {
    if (!searchTerm.trim()) return
    analytics.search({ searchTerm, resultsCount })
  }, [])

  const login = useCallback((method: string = 'phone') => {
    analytics.login({ method })
  }, [])

  const signUp = useCallback((method: string = 'phone') => {
    analytics.signUp({ method })
  }, [])

  const addToWishlist = useCallback((product: ProductInput) => {
    const item = toAnalyticsProduct(product)
    if (!item) return
    
    analytics.addToWishlist({ item })
  }, [])

  const applyPromoCode = useCallback(
    (coupon: string, success: boolean, discount?: number) => {
      if (!coupon.trim()) return
      analytics.applyPromoCode({ coupon, success, discount })
    },
    []
  )

  const trackEvent = useCallback(
    (eventName: string, params?: Record<string, unknown>) => {
      if (!eventName.trim()) return
      analytics.trackEvent(eventName, params)
    },
    []
  )

  // User management
  const setUserId = useCallback((userId: string | null) => {
    analytics.setUserId(userId)
  }, [])

  const setUserProperties = useCallback(
    (properties: Record<string, unknown>) => {
      analytics.setUserProperties(properties)
    },
    []
  )

  return useMemo(
    () => ({
      // Ecommerce
      viewItemList,
      viewItem,
      selectItem,
      addToCart,
      removeFromCart,
      viewCart,
      beginCheckout,
      addShippingInfo,
      addPaymentInfo,
      purchase,
      // Кастомные
      search,
      login,
      signUp,
      addToWishlist,
      applyPromoCode,
      trackEvent,
      // User
      setUserId,
      setUserProperties,
    }),
    [
      viewItemList,
      viewItem,
      selectItem,
      addToCart,
      removeFromCart,
      viewCart,
      beginCheckout,
      addShippingInfo,
      addPaymentInfo,
      purchase,
      search,
      login,
      signUp,
      addToWishlist,
      applyPromoCode,
      trackEvent,
      setUserId,
      setUserProperties,
    ]
  )
}

export default useAnalytics
